import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useMatches, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import type { RestaurantContext } from '@/bindings/RestaurantContext'
import { FormError } from '@/components/form-error'
import { Page } from '@/components/layout'
import { restaurantContextQuery } from '@/features/back-office/api'
import { branchHome } from '@/features/back-office/branch-home'
import { BRANCH_SCOPE_ROUTE, useManagedBranch, useRestaurantScope } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { canManageBranch, canManageRestaurant } from '@/lib/roles'

/** The one page a closed branch keeps (2026-09-12); the layout blocks every other branch page. */
const STATISTICS_ROUTE_PREFIX = `${BRANCH_SCOPE_ROUTE}/statistics`
/** The branch index only redirects (to the statistics when closed), so it passes too. */
const BRANCH_INDEX_ROUTE = `${BRANCH_SCOPE_ROUTE}/`
const LINK = 'text-accent-ink self-start text-sm font-medium hover:underline'

/** `/<r>/<b>/manage` forwards to the role's branch home; staff on a closed branch meet the closed notice. */
export function BranchManageIndex() {
  const { restaurantSlug, branchSlug, branch, context } = useManagedBranch()
  const navigate = useNavigate()
  const to = branchHome(context.role, branch.deactivated_at != null) ?? '/$restaurantSlug/$branchSlug/manage/queue'
  // An effect, not `<Navigate>`: that one re-fires on every render, and this page re-renders while the move is pending.
  useEffect(() => {
    void navigate({ to, params: { restaurantSlug, branchSlug }, replace: true })
  }, [navigate, to, restaurantSlug, branchSlug])
  return null
}

/**
 * Loads the restaurant once (by slug) for every page beneath; pages then read
 * the same cache. A role-less user gets the 403 verdict here, an unknown slug
 * the 404. At branch level the branch must be one the context lists.
 *
 * 2026-09-12: a deactivated restaurant renders the lock notice instead of any
 * page; a closed branch renders only its statistics pages.
 */
export function RestaurantLayout({ level }: { level: 'restaurant' | 'branch' }) {
  const t = useT()
  const scope = useRestaurantScope()
  const context = useQuery({ ...restaurantContextQuery(scope?.restaurantSlug ?? ''), enabled: scope !== undefined })
  const onStatistics = useMatches({
    select: (matches) => matches.some((match) => match.routeId.startsWith(STATISTICS_ROUTE_PREFIX) || match.routeId === BRANCH_INDEX_ROUTE),
  })

  const deactivated = context.data?.settings.deactivated_at != null
  const closed = level === 'branch' && scope?.branch?.deactivated_at != null && !onStatistics
  if (context.data && !deactivated && !closed && (level === 'restaurant' || scope?.branch)) return <Outlet />
  return (
    <Page width="md" className="flex flex-col gap-4">
      {context.error ? (
        <>
          <FormError error={context.error} />
          <Link to="/app/restaurants" className={LINK}>
            ← {t('backOffice.title')}
          </Link>
        </>
      ) : deactivated ? (
        <>
          <p role="alert" className="text-sm">
            {t('backOffice.restaurantDeactivated')}
          </p>
          <Link to="/app/restaurants" className={LINK}>
            ← {t('backOffice.title')}
          </Link>
        </>
      ) : closed && scope?.branch && context.data ? (
        <>
          <p className="text-muted text-sm">{t('backOffice.branchClosed')}</p>
          {canManageBranch(context.data.role) ? (
            <Link
              to="/$restaurantSlug/$branchSlug/manage/statistics"
              params={{ restaurantSlug: scope.restaurantSlug, branchSlug: scope.branch.slug }}
              search={{ tab: 'date' }}
              className={LINK}
            >
              {t('nav.statistics')} →
            </Link>
          ) : (
            <BackToRestaurant restaurantSlug={scope.restaurantSlug} context={context.data} />
          )}
        </>
      ) : context.data && scope ? (
        <>
          <p className="text-muted text-sm">{t('backOffice.branchNotInScope')}</p>
          <BackToRestaurant restaurantSlug={scope.restaurantSlug} context={context.data} />
        </>
      ) : (
        <p className="text-muted text-sm">{t('common.loading')}</p>
      )}
    </Page>
  )
}

/**
 * A dead end's way out: manager and above to the Branches page, admin and staff to the
 * restaurant landing page, where their way into a branch lives (2026-09-14).
 */
function BackToRestaurant({ restaurantSlug, context }: { restaurantSlug: string; context: RestaurantContext }) {
  const t = useT()
  return canManageRestaurant(context.role) ? (
    <Link to="/$restaurantSlug/manage/branches" params={{ restaurantSlug }} className={LINK}>
      ← {t('nav.branches')}
    </Link>
  ) : (
    <Link to="/$restaurantSlug" params={{ restaurantSlug }} className={LINK}>
      ← {t('landing.allBranches', { name: context.settings.name })}
    </Link>
  )
}
